import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map, Observable, of, throwError} from 'rxjs';
import {ServerEndpoints} from '../../../server.endpoints';
import {
  AreaUpdate,
  BackgroundPlacementUpdate,
  FloorArea,
  FloorPlanBackground,
  FloorTable,
  normalizeArea,
  normalizeBackground,
  normalizeTable,
  TableUpdate
} from '../shared/models/table-layout.model';

/**
 * Thin HTTP client for the monolith's persistent-table and floor-plan-area CRUD
 * (`/booking/Table/*`, `/booking/Area/*`, both shard-scoped through the caller's context token). Requests go
 * through the gateway under the SPA's single `BASE_URL` origin, so the auth interceptor attaches the token
 * automatically (mirrors {@link DeviceConfigHttpService}).
 *
 * Every response is normalized on the way in: the monolith serializes with `NullValueHandling.Ignore`, so nullable
 * members (`areaId`, `idleMinutes`, `description`) are simply absent rather than `null`.
 */
@Injectable({providedIn: 'root'})
export class TableLayoutHttpService {
  constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
  }

  /** The shard's persistent tables (`Temporary == false`; the backend scopes `List` for us). */
  listTables(): Observable<FloorTable[]> {
    return this.http.get<Partial<FloorTable>[]>(this.baseUrl + ServerEndpoints.Booking_Table_List)
      .pipe(map(tables => (tables ?? []).map(normalizeTable)));
  }

  /**
   * Writes the whole table set in one commit — the editor's save. The backend batches it into a single
   * transaction (`UpsertMany`), so a partially applied floor plan is not possible.
   */
  upsertTables(updates: TableUpdate[]): Observable<FloorTable[]> {
    return this.http.post<Partial<FloorTable>[]>(this.baseUrl + ServerEndpoints.Booking_Table_UpsertList, updates)
      .pipe(map(tables => (tables ?? []).map(normalizeTable)));
  }

  deleteTable(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}${ServerEndpoints.Booking_Table_Delete}?id=${encodeURIComponent(id)}`);
  }

  /** The shard's floor-plan areas. */
  listAreas(): Observable<FloorArea[]> {
    return this.http.get<Partial<FloorArea>[]>(this.baseUrl + ServerEndpoints.Booking_Area_List)
      .pipe(map(areas => (areas ?? []).map(normalizeArea)));
  }

  /** Writes the whole area set in one commit (see {@link upsertTables}). */
  upsertAreas(updates: AreaUpdate[]): Observable<FloorArea[]> {
    return this.http.post<Partial<FloorArea>[]>(this.baseUrl + ServerEndpoints.Booking_Area_UpsertList, updates)
      .pipe(map(areas => (areas ?? []).map(normalizeArea)));
  }

  deleteArea(id: string): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}${ServerEndpoints.Booking_Area_Delete}?id=${encodeURIComponent(id)}`);
  }

  // region Background image

  /**
   * The shard's background-image metadata (placement, opacity, fetch route), or `null` when it has none.
   *
   * A shard without a background is the normal, expected state, and the backend answers it with **404** — mapped to
   * `null` here rather than left as an error, so the caller does not have to treat "nothing configured" as a failure.
   */
  getBackground(): Observable<FloorPlanBackground | null> {
    return this.http.get<Partial<FloorPlanBackground>>(this.baseUrl + ServerEndpoints.Booking_FloorPlan_Get)
      .pipe(
        map(background => background ? normalizeBackground(background) : null),
        catchError((error: HttpErrorResponse) => error.status === 404 ? of(null) : throwError(() => error))
      );
  }

  /**
   * Uploads (or replaces) the background image.
   *
   * @param file The image file; PNG/JPG/JPEG/SVG, at most 10 MB (checked before the call — see
   *   {@link BACKGROUND_MAX_BYTES}).
   * @param placement Optional placement. **Omit it on a replacement** to inherit the previous image's position,
   *   width and opacity — re-exporting a corrected plan then does not force a re-align.
   */
  uploadBackground(file: File, placement?: BackgroundPlacementUpdate): Observable<FloorPlanBackground> {
    const body = new FormData();
    body.append('file', file, file.name);
    if (placement) {
      // Sibling form fields, not a nested object: that is what the multipart endpoint binds.
      body.append('x', String(Math.round(placement.x)));
      body.append('y', String(Math.round(placement.y)));
      body.append('width', String(Math.round(placement.width)));
      body.append('opacity', String(placement.opacity));
    }
    // No explicit Content-Type: the browser has to add the multipart boundary itself.
    return this.http.post<Partial<FloorPlanBackground>>(
      this.baseUrl + ServerEndpoints.Booking_FloorPlan_Upload, body).pipe(map(normalizeBackground));
  }

  /** Repositions / restyles the stored background — no new file. */
  updateBackgroundPlacement(placement: BackgroundPlacementUpdate): Observable<FloorPlanBackground> {
    return this.http.post<Partial<FloorPlanBackground>>(this.baseUrl + ServerEndpoints.Booking_FloorPlan_Update, {
      x: Math.round(placement.x),
      y: Math.round(placement.y),
      width: Math.round(placement.width),
      opacity: placement.opacity
    }).pipe(map(normalizeBackground));
  }

  /**
   * Fetches the background image's **bytes** as a blob.
   *
   * `/booking/FloorPlan/Image` is a role-gated API route, not a public blob URL, so the bytes have to come through
   * this HTTP client (where the auth interceptor attaches the bearer token and the `CM-Secret` cookie rides along) —
   * pointing an `<img src>` at the route fires an unauthenticated request that 401s. The caller turns the blob into
   * an object URL. Mirrors how the Forms module downloads submission attachments.
   */
  downloadBackgroundImage(): Observable<Blob> {
    return this.http.get(this.baseUrl + ServerEndpoints.Booking_FloorPlan_Image, {responseType: 'blob'});
  }

  /** Removes the background image (row and blob). */
  deleteBackground(): Observable<void> {
    return this.http.delete<void>(this.baseUrl + ServerEndpoints.Booking_FloorPlan_Delete);
  }

  // endregion
}
